// Improved lazy loading with Intersection Observer fallback
(function () {
  let retryCount = 0;
  const maxRetries = 5;
  let observer = null;
  const processedImages = new WeakSet();

  // Check for native lazy loading support
  const supportsNativeLazy = 'loading' in HTMLImageElement.prototype;
  const supportsIntersectionObserver = 'IntersectionObserver' in window;

  // Function to load an image
  function loadImage(imgElement) {
    // Skip if already processed
    if (processedImages.has(imgElement)) {
      return;
    }
    processedImages.add(imgElement);

    // Get the data attributes
    const dataSrc = imgElement.getAttribute('data-src');
    const dataSrcset = imgElement.getAttribute('data-srcset');
    const dataSizes = imgElement.getAttribute('data-sizes');

    if (!dataSrc) {
      console.warn('Lazy image has no data-src attribute', imgElement);
      return;
    }

    // Set up load event (only once)
    imgElement.addEventListener(
      'load',
      function onLoad() {
        imgElement.classList.add('loaded');
        imgElement.classList.remove('lazy');
        // Remove from observer if using it
        if (observer && !supportsNativeLazy) {
          observer.unobserve(imgElement);
        }
      },
      {once: true},
    );

    // Set up error event (only once)
    imgElement.addEventListener(
      'error',
      function onError() {
        console.warn('Failed to load image:', dataSrc);
        imgElement.classList.add('error');
        imgElement.classList.remove('lazy');
        // Remove from observer if using it
        if (observer && !supportsNativeLazy) {
          observer.unobserve(imgElement);
        }
      },
      {once: true},
    );

    // Apply srcset and sizes if available
    if (dataSrcset) {
      imgElement.setAttribute('srcset', dataSrcset);
    }

    if (dataSizes) {
      imgElement.setAttribute('sizes', dataSizes);
    }

    // Set the source to load the image
    imgElement.setAttribute('src', dataSrc);
  }

  // Function to initialize lazy loading
  function initLazyLoading() {
    // Select all unprocessed lazy images
    const lazyImages = document.querySelectorAll(
      '.lazy:not(.loaded):not(.error)',
    );

    if (lazyImages.length === 0) {
      // Only retry if we haven't exceeded max retries and DOM is still loading
      if (retryCount < maxRetries && document.readyState === 'loading') {
        retryCount++;
        console.log(
          `No lazy images found, retrying (${retryCount}/${maxRetries})...`,
        );
        setTimeout(initLazyLoading, 500);
        return;
      } else {
        console.log(
          'No lazy images found on this page, skipping lazy loading initialization',
        );
        return;
      }
    }

    // Use native lazy loading if supported
    if (supportsNativeLazy) {
      lazyImages.forEach(function (lazyImage) {
        if (lazyImage.tagName !== 'IMG') return;
        const imgElement = lazyImage;

        // Set native lazy loading
        imgElement.setAttribute('loading', 'lazy');
        loadImage(imgElement);
      });
    }
    // Use Intersection Observer for better performance
    else if (supportsIntersectionObserver) {
      // Create observer if it doesn't exist
      if (!observer) {
        observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                const imgElement = entry.target;
                loadImage(imgElement);
              }
            });
          },
          {
            // Start loading when image is 100px away from viewport
            rootMargin: '100px 0px',
            threshold: 0.01,
          },
        );
      }

      // Observe each lazy image
      lazyImages.forEach(function (lazyImage) {
        if (lazyImage.tagName !== 'IMG') return;
        observer.observe(lazyImage);
      });
    }
    // Fallback: load all images immediately for older browsers
    else {
      lazyImages.forEach(function (lazyImage) {
        if (lazyImage.tagName !== 'IMG') return;
        loadImage(lazyImage);
      });
    }

    // Set a class on html element to indicate lazy loading is initialized
    document.documentElement.classList.add('lazy-initialized');
  }

  // Expose the function globally for dynamic content
  // @ts-ignore - Adding to window object
  window.initLazyLoading = initLazyLoading;

  // Function to handle dynamically added content
  // @ts-ignore - Adding to window object
  window.refreshLazyLoading = function () {
    // Reset retry count for new attempt
    retryCount = 0;
    initLazyLoading();
  };

  // Initialize when DOM is ready or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLazyLoading);
  } else {
    initLazyLoading();
  }

  // Optional: Watch for dynamically added content
  if ('MutationObserver' in window) {
    const mutationObserver = new MutationObserver(function (mutations) {
      let hasNewImages = false;
      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) {
              // Element node
              const element = node;
              // Check if the added node is a lazy image or contains lazy images
              // @ts-ignore - Element type checking
              if (element.classList && element.classList.contains('lazy')) {
                hasNewImages = true;
                // @ts-ignore - Element type checking
              } else if (element.querySelectorAll) {
                // @ts-ignore - Element type checking
                const newLazyImages = element.querySelectorAll('.lazy');
                if (newLazyImages.length > 0) {
                  hasNewImages = true;
                }
              }
            }
          });
        }
      });

      if (hasNewImages) {
        // Debounce the initialization to avoid excessive calls
        // @ts-ignore - Adding timeout to window
        clearTimeout(window.lazyLoadingTimeout);
        // @ts-ignore - Adding timeout to window
        window.lazyLoadingTimeout = setTimeout(function () {
          // @ts-ignore - Function exists on window
          window.refreshLazyLoading();
        }, 100);
      }
    });

    // Start observing
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
